import "./CobranzaPage.css";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CalendarClock, CheckCircle2, CircleDollarSign,
  FileText, RefreshCw, Search, Wallet, Printer, ArrowLeft,
  UserRound, XCircle
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import HistoricalSalesPage from "@/pages/HistoricalSalesPage";
import { useAuth } from "@/context/AuthContext";

const money = (v, currency = "USD") =>
  `${currency} ${Number(v || 0).toLocaleString("es-BO", {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  })}`;

const dateText = (v) => {
  if (!v) return "Sin fecha";
  const d = new Date(`${String(v).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("es-BO");
};

export default function CobranzaPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "administrador";
  const [tab, setTab] = useState("resumen");
  const [sales, setSales] = useState([]);
  const [historical, setHistorical] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [paymentModal, setPaymentModal] = useState(false);
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [reportClient, setReportClient] = useState("");
  const [reportOrigin, setReportOrigin] = useState("todos");
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [payment, setPayment] = useState({
    amount: "", payment_date: new Date().toISOString().slice(0, 10),
    method: "Transferencia", reference: "", concept: "Pago de cuota",
    notes: "", currency: "USD", exchange_rate: "6.96"
  });

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const results = await Promise.allSettled([
        api.get("/cobranzas/ventas"),
        api.get("/cobranzas/historicas")
      ]);
      if (results[0].status === "fulfilled") setSales(results[0].value.data || []);
      else throw results[0].reason;
      setHistorical(results[1].status === "fulfilled" ? results[1].value.data || [] : []);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const allRows = useMemo(() => [
    ...sales.map(s => ({ ...s, recordType: "actual", displayName: s.client_name || s.sale?.client_name || "Cliente sin nombre" })),
    ...historical.map(s => ({ ...s, recordType: "historica", displayName: s.owner_name || "Propietario sin nombre" }))
  ], [sales, historical]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(s => [
      s.displayName, s.original_sale_number, s.id,
      s.lot?.manzano, s.lot?.lote, s.manzano, s.lote
    ].some(v => String(v || "").toLowerCase().includes(q)));
  }, [allRows, query]);

  const totalReceivable = allRows.reduce((sum, s) => {
    const balance = s.accounting_balance ?? s.saldo_contable;
    return sum + (balance == null || balance === "" ? 0 : Number(balance));
  }, 0);
  const appliedPayments = allRows.reduce((sum, s) =>
    sum + Number(s.payments_applied ?? s.pagos_aplicados ?? s.pagos_verificados ?? 0), 0);

  const openRecord = async (row) => {
    setSelected(row);
    setDetail(null);
    setNotice("");
    setError("");
    setDetailLoading(true);
    try {
      const path = row.recordType === "historica"
        ? `/cobranzas/historicas/${encodeURIComponent(row.id)}`
        : `/cobranzas/ventas/${encodeURIComponent(row.id)}`;
      const res = await api.get(path);
      setDetail({ ...res.data, recordType: row.recordType });
      setPayment((current) => ({
        ...current,
        currency: row.recordType === "historica"
          ? "USD"
          : (res.data.account?.currency || current.currency || "USD"),
        exchange_rate: current.exchange_rate || "6.96",
      }));
      setTab("cartera");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    if (!selected || !detail || !payment.amount || Number(payment.amount) <= 0) {
      setError("Selecciona una venta y escribe un importe válido.");
      return;
    }
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const path = selected.recordType === "historica"
        ? `/cobranzas/historicas/${encodeURIComponent(selected.id)}/pagos-historicos`
        : `/cobranzas/ventas/${encodeURIComponent(selected.id)}/pagos`;
      const body = selected.recordType === "historica"
        ? {
            amount: Number(payment.amount), payment_date: payment.payment_date,
            method: payment.method, reference: payment.reference,
            concept: payment.concept, notes: payment.notes,
            currency: payment.currency,
            exchange_rate: Number(payment.exchange_rate)
          }
        : { ...payment, amount: Number(payment.amount) };
      await api.post(path, body);
      setPaymentModal(false);
      setPayment(p => ({ ...p, amount: "", reference: "", notes: "" }));
      await loadData();
      await openRecord(selected);
      setNotice("Pago registrado y aplicado automáticamente por FIFO. El saldo fue actualizado.");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const voidPayment = async (paymentRow) => {
    const reason = window.prompt("Motivo obligatorio para anular este pago:");
    if (!reason || reason.trim().length < 3) return;
    setLoading(true);
    setError("");
    try {
      const path = selected.recordType === "historica"
        ? `/cobranzas/historicas/pagos/${encodeURIComponent(paymentRow.id)}/anular`
        : `/cobranzas/pagos/${encodeURIComponent(paymentRow.id)}/anular`;
      await api.post(path, { reason: reason.trim() });
      await loadData();
      await openRecord(selected);
      setNotice("Pago anulado. Sus aplicaciones FIFO fueron revertidas del saldo.");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const printHistoricalReceipt = async (paymentRow) => {
    const w = window.open("", "_blank", "width=900,height=850");
    if (!w) {
      setError("El navegador bloqueó la ventana. Permite las ventanas emergentes.");
      return;
    }

    const esc = (value) => String(value ?? "—").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]));

    try {
      w.document.write("<p style='font-family:Arial;padding:30px'>Preparando comprobante...</p>");

      const response = await api.get(
        `/cobranzas/historicas/pagos/${encodeURIComponent(paymentRow.id)}/comprobante`
      );
      const r = response.data || {};
      const lot = r.lote || {};
      const account = r.estado_cuenta || {};
      const currency = account.moneda || r.moneda || "USD";
      const isVoided = r.estado === "ANULADO";
      const isOverdue = account.estado === "VENCIDO";
      const isCompleted = account.estado === "PLAN_CONCLUIDO";
      const isUpToDate = account.estado === "AL_DIA";

      let statusPanel = "";

      if (isVoided) {
        statusPanel = `
          <section class="account-status neutral">
            <div class="status-heading">ESTADO DE CUENTA</div>
            <strong>Este comprobante corresponde a un pago anulado.</strong>
            <p>El estado de cuenta refleja únicamente los pagos activos.</p>
          </section>`;
      } else if (isOverdue) {
        statusPanel = `
          <section class="account-status overdue">
            <div class="status-heading">ESTADO DE CUENTA · CUOTAS VENCIDAS</div>
            <div class="status-grid">
              <div><small>CUOTAS VENCIDAS</small><strong>${esc(account.cuotas_vencidas)}</strong></div>
              <div><small>MONTO VENCIDO</small><strong>${esc(money(account.monto_vencido, currency))}</strong></div>
              <div><small>CUOTAS PENDIENTES EN TOTAL</small><strong>${esc(account.cuotas_pendientes)}</strong></div>
              <div><small>MONTO PENDIENTE TOTAL</small><strong>${esc(money(account.monto_pendiente, currency))}</strong></div>
            </div>
            <p>Le recomendamos regularizar las cuotas vencidas para mantener su cuenta al día.</p>
          </section>`;
      } else if (isUpToDate) {
        statusPanel = `
          <section class="account-status current">
            <div class="status-heading">ESTADO DE CUENTA · AL DÍA</div>
            <strong class="current-title">¡Gracias por mantener sus pagos al día!</strong>
            <p>Recuerde que su próxima fecha de pago es:</p>
            <div class="next-date">${esc(dateText(account.proxima_fecha_pago))}</div>
            ${account.proxima_cuota ? `<small>${esc(account.proxima_cuota)}</small>` : ""}
            <div class="pending-total">Saldo pendiente del plan: ${esc(money(account.monto_pendiente, currency))}</div>
          </section>`;
      } else if (isCompleted) {
        statusPanel = `
          <section class="account-status current">
            <div class="status-heading">ESTADO DE CUENTA</div>
            <strong class="current-title">¡Plan de pagos concluido!</strong>
            <p>No registra cuotas pendientes en este plan.</p>
          </section>`;
      } else {
        statusPanel = `
          <section class="account-status neutral">
            <div class="status-heading">ESTADO DE CUENTA</div>
            <strong>Estado no disponible</strong>
          </section>`;
      }

      const logoUrl = `${window.location.origin}/puerto-santa-cruz-logo.png`;

      w.document.open();
      w.document.write(`<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Comprobante de registro interno ${esc(r.numero)}</title>
<style>
  *{box-sizing:border-box}
  body{margin:0;background:#eef2f6;color:#243447;font-family:Arial,Helvetica,sans-serif}
  .sheet{width:215.9mm;height:279.4mm;min-height:279.4mm;margin:22px auto;background:#fff;padding:11mm 13mm 8mm;box-shadow:0 5px 28px rgba(20,40,60,.12)}
  .top-line{height:6px;background:#173c5b;margin:-11mm -13mm 13px}
  .header{display:flex;align-items:center;gap:16px;padding:5px 0 11px;border-bottom:2px solid #dce5ec}
  .logo{width:90px;height:65px;object-fit:contain}
  .brand{flex:1}
  .brand h1{font-size:22px;letter-spacing:1.3px;color:#173c5b;margin:0 0 5px}
  .brand .club{font-size:10px;letter-spacing:2px;color:#748395;font-weight:bold}
  .brand .document-name{font-size:12px;font-weight:bold;letter-spacing:1px;color:#34495e;margin-top:13px}
  .document-number{text-align:right;min-width:150px}
  .document-number small{display:block;font-size:9px;color:#8492a2;letter-spacing:1px;margin-bottom:7px}
  .document-number strong{font-size:12px;color:#173c5b;overflow-wrap:anywhere}
  .amount-card{margin-top:13px;padding:12px 17px;background:#f3f7fa;border:1px solid #dce5ec;border-left:5px solid #173c5b;border-radius:7px}
  .amount-card small{font-size:9px;letter-spacing:1.5px;color:#6e7d8d;font-weight:bold}
  .amount{font-size:25px;font-weight:bold;color:#173c5b;margin-top:8px}
  .section{margin-top:14px}
  .section-title{font-size:10px;letter-spacing:1.3px;color:#173c5b;font-weight:bold;padding-bottom:9px;border-bottom:1px solid #dce5ec;margin-bottom:13px}
  .data-grid{display:grid;grid-template-columns:1fr 1fr;column-gap:25px;row-gap:11px}
  .field small{display:block;font-size:9px;color:#8794a2;margin-bottom:5px;text-transform:uppercase;letter-spacing:.5px}
  .field strong{display:block;font-size:12px;color:#27384a;overflow-wrap:anywhere}
  .payment-status{margin-top:12px;padding:8px 12px;border-radius:5px;background:#edf4f8;border:1px solid #d5e3ec;font-size:11px}
  .account-status{margin-top:14px;padding:12px 15px;border-radius:7px;break-inside:avoid}
  .status-heading{font-size:10px;letter-spacing:1.2px;font-weight:bold;margin-bottom:14px}
  .status-grid{display:grid;grid-template-columns:1fr 1fr;gap:11px;margin:8px 0}
  .status-grid small{display:block;font-size:9px;letter-spacing:.5px;margin-bottom:6px}
  .status-grid strong{font-size:19px}
  .account-status p{font-size:11px;line-height:1.6;margin:10px 0 0}
  .overdue{background:#fff5f2;border:1px solid #f0c7bc;color:#923c2b}
  .overdue .status-heading{color:#a53e2c}
  .current{background:#eff8f3;border:1px solid #c9e4d3;color:#246344}
  .current .status-heading{color:#28734d}
  .current-title{font-size:16px}
  .next-date{font-size:23px;font-weight:bold;margin-top:9px;color:#246344}
  .pending-total{font-size:10px;margin-top:12px;padding-top:10px;border-top:1px solid #c9e4d3}
  .neutral{background:#f2f5f8;border:1px solid #dce3e9;color:#526273}
  .notice{margin-top:12px;padding:8px 12px;background:#fff8e9;border:1px solid #ead8a9;border-radius:5px;color:#775d27;font-size:10px;line-height:1.6}
  .notice strong{display:block;font-size:10px;letter-spacing:.5px;margin-bottom:3px}
  .signatures{display:grid;grid-template-columns:1fr 1fr;gap:45px;margin-top:25px}
  .signature{border-top:1px solid #9aa8b5;padding-top:8px;text-align:center;font-size:9px;color:#6e7d8d}
  .footer{border-top:1px solid #dce5ec;margin-top:20px;padding-top:8px;text-align:center;font-size:9px;line-height:1.7;color:#8492a2}
  @page{size:letter;margin:0}
  @media print{
    body{background:#fff}
    .sheet{width:215.9mm;height:279.4mm;min-height:279.4mm;margin:0;box-shadow:none}
    .top-line{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .amount-card,.account-status,.notice{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  }
  @media screen and (max-width:760px){.sheet{width:100%;padding:24px}.top-line{margin:-24px -24px 20px}.header{flex-wrap:wrap}.document-number{text-align:left}}
</style>
</head>
<body>
<main class="sheet">
  <div class="top-line"></div>
  <header class="header">
    <img class="logo" src="${esc(logoUrl)}" alt="Puerto Santa Cruz">
    <div class="brand">
      <h1>PUERTO SANTA CRUZ</h1>
      <div class="club">YACHT CLUB RESORT</div>
      <div class="document-name">COMPROBANTE DE REGISTRO INTERNO</div>
    </div>
    <div class="document-number">
      <small>NÚMERO DE REGISTRO</small>
      <strong>${esc(r.numero)}</strong>
    </div>
  </header>

  <section class="amount-card">
    <small>IMPORTE DEL PAGO REGISTRADO</small>
    <div class="amount">${esc(money(r.importe, r.moneda))}</div>
  </section>

  <section class="section">
    <div class="section-title">INFORMACIÓN DEL TITULAR Y DEL TERRENO</div>
    <div class="data-grid">
      <div class="field"><small>Propietario / Pagador</small><strong>${esc(r.pagador)}</strong></div>
      <div class="field"><small>Número de venta</small><strong>${esc(r.venta)}</strong></div>
      <div class="field"><small>Manzano</small><strong>${esc(lot.manzano)}</strong></div>
      <div class="field"><small>Lote</small><strong>${esc(lot.lote)}</strong></div>
    </div>
  </section>

  <section class="section">
    <div class="section-title">DETALLE DE LA OPERACIÓN</div>
    <div class="data-grid">
      <div class="field"><small>Fecha de pago</small><strong>${esc(dateText(r.fecha_pago))}</strong></div>
      <div class="field"><small>Fecha de registro</small><strong>${esc(dateText(r.fecha_registro))}</strong></div>
      <div class="field"><small>Concepto</small><strong>${esc(r.concepto)}</strong></div>
      ${r.moneda === "BOB" ? `
        <div class="field"><small>Tipo de cambio aplicado</small><strong>Bs ${esc(r.tipo_cambio)} por USD</strong></div>
        <div class="field"><small>Equivalente aplicado al contrato</small><strong>${esc(money(r.equivalente_contrato, r.moneda_contrato || "USD"))}</strong></div>
      ` : `
        <div class="field"><small>Equivalente aplicado al contrato</small><strong>${esc(money(r.equivalente_contrato ?? r.importe, r.moneda_contrato || "USD"))}</strong></div>
      `}
      <div class="field"><small>Método de pago</small><strong>${esc(r.metodo)}</strong></div>
      <div class="field"><small>Referencia</small><strong>${esc(r.referencia)}</strong></div>
      <div class="field"><small>Registrado por</small><strong>${esc(r.registrado_por)}</strong></div>
    </div>
    <div class="payment-status"><strong>Estado del pago:</strong> ${esc(r.estado)}</div>
    ${r.nota ? `<p style="font-size:11px;line-height:1.5"><strong>Observación:</strong> ${esc(r.nota)}</p>` : ""}
  </section>

  ${statusPanel}

  <div class="notice">
    <strong>DOCUMENTO DE REGISTRO INTERNO</strong>
    ${esc(r.aviso || "Este documento acredita el registro interno del pago y no constituye factura fiscal.")}
  </div>

  <div class="signatures">
    <div class="signature">Firma del cliente</div>
    <div class="signature">Administración · Puerto Santa Cruz</div>
  </div>

  <footer class="footer">
    PUERTO SANTA CRUZ · YACHT CLUB RESORT<br>
    Sistema de Gestión y Cobranzas · Documento generado electrónicamente
  </footer>
</main>
<script>
  window.onload = () => {
    const logo = document.querySelector(".logo");
    const print = () => setTimeout(() => window.print(), 250);
    if (logo.complete) print();
    else {
      logo.onload = print;
      logo.onerror = print;
    }
  };
</script>
</body>
</html>`);
      w.document.close();
    } catch (e) {
      w.document.open();
      w.document.write(
        `<p style="font-family:Arial;padding:30px;color:#b91c1c">No se pudo obtener el comprobante: ${esc(apiError(e))}</p>`
      );
      w.document.close();
      setError(apiError(e));
    }
  };

  const printStatement = () => {
    if (!detail || !selected) return;
    const account = detail.account || detail.sale || {};
    const currency = account.currency || selected.currency || "USD";
    const payments = detail.payments || [];
    const initial = account.initial_installment;
    const initialRow = initial ? `<tr><td>0</td><td>${initial.concept || "Cuota inicial histórica"}</td><td>${dateText(initial.due_date)}</td><td>${money(initial.amount, currency)}</td><td>Estado documental: ${initial.source_status || "No indicado"}</td></tr>` : "";
    const rows = (detail.obligaciones || [])
      .map(i => `<tr><td>${i.installment ?? "—"}</td><td>${i.concept || "Obligación"}</td><td>${dateText(i.due_date)}</td><td>${money(i.amount, currency)}</td><td>${money(i.paid, currency)}</td><td>${money(i.outstanding, currency)}</td></tr>`).join("");
    const paymentRows = payments.map(p =>
      `<tr><td>${dateText(p.payment_date)}</td><td>${p.concept || "Pago"}</td><td>${money(p.amount, p.currency || currency)}</td><td>${p.status === "void" ? "ANULADO" : "APLICADO"}</td></tr>`
    ).join("");
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { setError("El navegador bloqueó la ventana de impresión. Permite las ventanas emergentes."); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Estado de cuenta PSC</title>
      <style>body{font:14px Arial,sans-serif;padding:28px;color:#172033}h1{font-size:22px}h2{font-size:16px;margin-top:26px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #ccd2dc;padding:8px;text-align:left}th{background:#eef2f7}.notice{border:1px solid #b45309;background:#fff7ed;padding:12px;margin:18px 0}.muted{color:#596579;font-size:12px}@media print{button{display:none}}</style>
      </head><body><h1>PUERTO SANTA CRUZ — ESTADO DE CUENTA</h1>
      <p><b>Cliente:</b> ${account.owner_name || account.client_name || selected.displayName || "—"}</p>
      <p><b>Venta:</b> ${account.original_sale_number || account.sale_number || selected.id || "—"}</p>
      <p><b>Manzano / lote:</b> ${(account.lot?.manzano || account.manzano || selected.lot?.manzano || "—")} / ${(account.lot?.lote || account.lote || selected.lot?.lote || "—")}</p>
      <p><b>Importe contractual:</b> ${money(detail.resumen?.monto_contratado ?? detail.summary?.contract_amount ?? account.contract_amount, currency)}</p>
      <p><b>Saldo contable:</b> ${money(detail.resumen?.saldo_contable ?? detail.summary?.accounting_balance, currency)}</p>
      ${selected.recordType === "historica" ? '<div class="notice"><b>REGISTRO HISTÓRICO:</b> el estado Pagado procede del documento histórico y no acredita por sí solo conciliación bancaria.</div>' : ''}
      <h2>Cuota inicial histórica</h2><table><thead><tr><th>N.º</th><th>Concepto</th><th>Fecha</th><th>Importe</th><th>Estado</th></tr></thead><tbody>${initialRow || '<tr><td colspan="5">No hay una cuota inicial histórica guardada.</td></tr>'}</tbody></table>
      <h2>Obligaciones y aplicaciones FIFO</h2><table><thead><tr><th>N.º</th><th>Concepto</th><th>Vencimiento</th><th>Importe</th><th>Aplicado</th><th>Saldo</th></tr></thead><tbody>${rows || '<tr><td colspan="6">No hay obligaciones disponibles.</td></tr>'}</tbody></table>
      <h2>Pagos registrados</h2><table><thead><tr><th>Fecha</th><th>Concepto</th><th>Importe</th><th>Estado de aplicación</th></tr></thead><tbody>${paymentRows || '<tr><td colspan="4">No hay pagos registrados.</td></tr>'}</tbody></table>
      <p class="muted">Documento informativo. Los pagos activos se aplican automáticamente por FIFO. Los estados del PDF son únicamente documentales.</p><script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  const generatePaymentReport = async () => {
    setReportLoading(true);
    setError("");
    setReportRows([]);
    try {
      const candidates = allRows.filter(row => reportOrigin === "todos" || row.recordType === reportOrigin);
      const details = await Promise.allSettled(candidates.map(async row => {
        const path = row.recordType === "historica"
          ? `/cobranzas/historicas/${encodeURIComponent(row.id)}`
          : `/cobranzas/ventas/${encodeURIComponent(row.id)}`;
        const response = await api.get(path);
        return { row, detail: response.data || {} };
      }));
      const needle = reportClient.trim().toLowerCase();
      const result = [];
      details.forEach(item => {
        if (item.status !== "fulfilled") return;
        const { row, detail: data } = item.value;
        const account = data.account || data.sale || {};
        const client = account.owner_name || account.client_name || row.displayName || "Cliente sin nombre";
        const sale = account.original_sale_number || account.sale_number || row.original_sale_number || row.id;
        const lot = account.lot || account.lot_snapshot || row.lot || {};
        if (needle && !`${client} ${sale} ${lot.manzano || ""} ${lot.lote || ""}`.toLowerCase().includes(needle)) return;
        (data.payments || []).forEach(p => {
          const date = String(p.payment_date || "").slice(0, 10);
          if (reportFrom && date < reportFrom) return;
          if (reportTo && date > reportTo) return;
          const currency = p.currency || account.currency || "USD";
          const rate = p.exchange_rate || p.tipo_cambio || null;
          const usd = p.amount_usd ?? p.amount_contract_currency ?? p.equivalent_contract_currency ?? p.equivalente_contrato ??
            (currency === "BOB" && rate ? Number(p.amount || 0) / Number(rate) : Number(p.amount || 0));
          result.push({
            id: p.id || `${row.id}-${date}-${result.length}`, payment_date: date, client, sale,
            manzano: lot.manzano || account.manzano || "—", lote: lot.lote || account.lote || "—",
            amount: Number(p.amount || 0), currency, exchange_rate: rate, amount_usd: Number(usd || 0),
            method: p.method || p.payment_method || "—",
            reference: p.reference || p.receipt_no || p.receipt_reference || "—",
            status: p.status === "void" ? "Anulado" : "Activo",
            origin: row.recordType === "historica" ? "Histórico" : "Actual"
          });
        });
      });
      result.sort((a,b) => b.payment_date.localeCompare(a.payment_date));
      setReportRows(result);
      setReportGenerated(true);
      setNotice(result.length ? `Reporte generado: ${result.length} movimiento(s).` : "No se encontraron pagos con los filtros seleccionados.");
    } catch (e) { setError(apiError(e)); }
    finally { setReportLoading(false); }
  };

  const exportPaymentReport = () => {
    const headers = ["Fecha","Cliente","Venta","Manzano","Lote","Importe recibido","Moneda","Tipo de cambio","Equivalente USD","Método","Referencia","Estado","Origen"];
    const rows = reportRows.map(r => [r.payment_date,r.client,r.sale,r.manzano,r.lote,r.amount,r.currency,r.exchange_rate || "",r.amount_usd,r.method,r.reference,r.status,r.origin]);
    const cell = v => '"' + String(v ?? "").replace(/"/g, '""') + '"';
    const csv = "﻿" + [headers,...rows].map(row => row.map(cell).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], {type:"text/csv;charset=utf-8;"}));
    const a = document.createElement("a");
    a.href = url; a.download = `PSC_reporte_pagos_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const printPaymentReport = () => {
    const w = window.open("", "_blank", "width=1200,height=800");
    if (!w) { setError("Permite las ventanas emergentes para imprimir el reporte."); return; }
    const esc = v => String(v ?? "—").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
    const rows = reportRows.map(r => `<tr><td>${esc(dateText(r.payment_date))}</td><td>${esc(r.client)}</td><td>${esc(r.sale)}</td><td>${esc(r.manzano)} / ${esc(r.lote)}</td><td>${esc(money(r.amount,r.currency))}</td><td>${r.currency === "BOB" ? esc(r.exchange_rate || "—") : "—"}</td><td>${esc(money(r.amount_usd,"USD"))}</td><td>${esc(r.method)}</td><td>${esc(r.status)}</td><td>${esc(r.origin)}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Reporte PSC</title><style>body{font:12px Arial;padding:24px;color:#183650}h1{font-size:21px}table{width:100%;border-collapse:collapse;margin-top:18px}th,td{border:1px solid #dce5ec;padding:8px;text-align:left}th{background:#eff5f9;font-size:10px}@media print{button{display:none}}</style></head><body><h1>PUERTO SANTA CRUZ · REPORTE DE PAGOS</h1><p>Período: ${esc(reportFrom || "Sin límite")} al ${esc(reportTo || "Sin límite")} · Cliente: ${esc(reportClient || "Todos")} · Origen: ${esc(reportOrigin)}</p><p>Movimientos: ${reportRows.length}</p><table><thead><tr><th>Fecha</th><th>Cliente</th><th>Venta</th><th>Manzano / lote</th><th>Recibido</th><th>Cambio</th><th>Equiv. USD</th><th>Método</th><th>Estado</th><th>Origen</th></tr></thead><tbody>${rows}</tbody></table><script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  const tabs = [
    ["resumen", "Resumen"], ["cartera", "Cartera"],
    ["reportes", "Reportes"], ["historico", "Importación histórica"]
  ];

  return <div className="collection-page">
    <style>{`
      .collection-page{padding:28px;max-width:1500px;margin:auto;color:var(--text-primary,#172033)}
      .collection-head{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:22px}
      .collection-head h1{margin:0 0 7px;font-size:28px}.collection-head p{margin:0;color:var(--text-secondary,#64748b)}
      .collection-tabs{display:flex;gap:8px;flex-wrap:wrap;border-bottom:1px solid var(--border,#dce2ea);margin-bottom:22px}
      .collection-tabs button{border:0;background:transparent;padding:12px 15px;cursor:pointer;color:inherit;border-bottom:3px solid transparent;font-weight:600}
      .collection-tabs button.active{border-color:#1769aa;color:#1769aa}
      .collection-cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(205px,1fr));gap:14px;margin-bottom:22px}
      .collection-card,.collection-panel{border:1px solid var(--border,#dce2ea);border-radius:14px;padding:18px;background:var(--surface,#fff);box-shadow:0 2px 8px #0f172a0a}
      .collection-card small{display:block;color:#64748b;margin-bottom:12px}.collection-card strong{font-size:24px;display:block}
      .collection-panel{margin-bottom:18px}.collection-panel h2{font-size:18px;margin:0 0 16px}
      .collection-tools{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:14px}
      .collection-input{border:1px solid #cbd5e1;border-radius:9px;padding:11px 12px;min-width:180px;max-width:100%;box-sizing:border-box}
      .collection-btn{display:inline-flex;align-items:center;gap:8px;border:1px solid #cbd5e1;background:#fff;border-radius:9px;padding:10px 13px;cursor:pointer;font-weight:600;color:#172033}
      .collection-btn.primary{background:#1769aa;border-color:#1769aa;color:#fff}.collection-btn:disabled{opacity:.55;cursor:not-allowed}
      .collection-table-wrap{overflow:auto}.collection-table{border-collapse:collapse;width:100%;min-width:720px}
      .collection-table th,.collection-table td{text-align:left;padding:12px;border-bottom:1px solid #e2e8f0;font-size:13px}
      .collection-table th{background:#f8fafc;color:#475569}.collection-link{border:0;background:none;color:#1769aa;font-weight:700;cursor:pointer;padding:0;text-align:left}
      .collection-badge{display:inline-block;padding:4px 8px;border-radius:20px;background:#fef3c7;color:#92400e;font-size:11px}
      .collection-alert{padding:12px 14px;border-radius:9px;margin:12px 0;background:#fff7ed;color:#9a3412}
      .collection-success{padding:12px 14px;border-radius:9px;margin:12px 0;background:#ecfdf5;color:#166534}
      .collection-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:12px}
      .collection-field{display:flex;flex-direction:column;gap:6px}.collection-field label{font-size:12px;font-weight:700;color:#475569}
      .collection-detail-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap}
      .collection-empty{padding:28px;text-align:center;color:#64748b}
      @media(max-width:650px){.collection-page{padding:15px}.collection-head h1{font-size:23px}.collection-card strong{font-size:20px}}
    `}</style>
    <header className="collection-head">
      <div><h1>Cobranza y Cartera</h1><p>Control de saldos, vencimientos, pagos y ventas históricas.</p></div>
      <button className="collection-btn" onClick={loadData} disabled={loading}><RefreshCw size={16}/> Actualizar</button>
    </header>
    <nav className="collection-tabs" aria-label="Secciones de cobranza">
      {tabs.map(([id, label]) => <button key={id} className={tab === id ? "active" : ""} onClick={() => { setTab(id); setError(""); }}>{label}</button>)}
    </nav>
    {error && <div className="collection-alert" role="alert"><AlertTriangle size={16} style={{verticalAlign:"middle"}}/> {error}</div>}
    {notice && <div className="collection-success"><CheckCircle2 size={16} style={{verticalAlign:"middle"}}/> {notice}</div>}

    {tab === "resumen" && <>
      <section className="collection-cards">
        <article className="collection-card"><small><Wallet size={16}/> Ventas en cartera</small><strong>{allRows.length}</strong><small>Ventas actuales e históricas</small></article>
        <article className="collection-card"><small><CircleDollarSign size={16}/> Saldo / importe informado</small><strong>{money(totalReceivable)}</strong><small>Conforme a los datos disponibles</small></article>
        <article className="collection-card"><small><CheckCircle2 size={16}/> Pagos aplicados</small><strong>{money(appliedPayments)}</strong><small>Aplicaciones FIFO registradas</small></article>
        <article className="collection-card"><small><CalendarClock size={16}/> Próximos vencimientos</small><strong>Por completar</strong><small>Se habilitarán con fechas y planes validados</small></article>
      </section>
      <section className="collection-panel"><h2>Acceso rápido a la cartera</h2>
        <div className="collection-tools"><input className="collection-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Cliente, venta, manzano o lote"/><button className="collection-btn primary" onClick={()=>setTab("cartera")}><Search size={16}/> Buscar cartera</button></div>
        <p>Selecciona una venta para consultar sus datos, plan de cuotas, pagos registrados e imprimir el estado de cuenta.</p>
      </section>
    </>}

    {(tab === "cartera" || tab === "pagos") && <>
      <section className="collection-panel">
        <div className="collection-tools"><input className="collection-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Buscar cliente, número de venta, manzano o lote"/><span style={{alignSelf:"center",color:"#64748b"}}>{filtered.length} registros</span></div>
        <div className="collection-table-wrap"><table className="collection-table"><thead><tr><th>Cliente / propietario</th><th>Venta</th><th>Manzano</th><th>Lote</th><th>Saldo informado</th><th>Origen</th><th>Acción</th></tr></thead><tbody>
          {filtered.map(s => {
            const balance = s.accounting_balance ?? s.saldo_contable;
            const manzano = s.lot?.manzano ?? s.lot_snapshot?.manzano ?? s.manzano ?? "—";
            const lote = s.lot?.lote ?? s.lot_snapshot?.lote ?? s.lote ?? "—";
            return <tr key={`${s.recordType}-${s.id}`}>
              <td><strong>{s.displayName}</strong></td>
              <td>{s.original_sale_number || s.id}</td>
              <td>{manzano || "—"}</td>
              <td>{lote || "—"}</td>
              <td>{balance == null || balance === ""
                ? <span title="El saldo debe calcularse o conciliarse">Pendiente de conciliación</span>
                : money(balance, s.currency)}</td>
              <td><span className="collection-badge">{s.recordType === "historica" ? "Histórico" : "Actual"}</span></td>
              <td>
                <div className="collection-tools">
                  <button className="collection-link" onClick={() => openRecord(s)}>Abrir detalle</button>
                  <button type="button" className="collection-link" onClick={async () => {
                    await openRecord(s);
                    setPaymentModal(true);
                  }}>Registrar pago</button>
                </div>
              </td>
            </tr>;
          })}
          {!filtered.length && <tr><td colSpan="7" className="collection-empty">{loading ? "Cargando cartera…" : "No se encontraron registros."}</td></tr>}
        </tbody></table></div>
      </section>
      {detailLoading && <section className="collection-panel">Cargando detalle de la venta…</section>}
      {detail && selected && <section className="collection-panel">
        <div className="collection-detail-head"><div><h2><UserRound size={18} style={{verticalAlign:"middle"}}/> {detail.account?.owner_name || detail.sale?.client_name || selected.displayName}</h2><p>Venta: {detail.account?.original_sale_number || detail.sale?.sale_number || selected.original_sale_number || selected.id} · {selected.recordType === "historica" ? "Histórica" : "Actual"}</p><p>Manzano: {detail.account?.lot?.manzano || detail.account?.manzano || detail.sale?.lot?.manzano || "—"} · Lote: {detail.account?.lot?.lote || detail.account?.lote || detail.sale?.lot?.lote || "—"}</p></div><div className="collection-tools"><button className="collection-btn" onClick={printStatement}><Printer size={16}/> Imprimir estado de cuenta</button><button className="collection-btn" onClick={()=>{setSelected(null);setDetail(null)}}><XCircle size={16}/> Cerrar detalle</button></div></div>
        <div className="collection-cards">
          <article className="collection-card">
            <small>Importe contractual</small>
            <strong>{money(detail.resumen?.monto_contratado ?? detail.summary?.contract_amount ?? detail.account?.contract_amount, detail.account?.currency || selected.currency)}</strong>
          </article>
          {selected.recordType === "historica" ? (
            <article className="collection-card">
              <small>Monto pagado / reconocido</small>
              <strong>{money(
                Number(detail.resumen?.pagos_aplicados ?? detail.summary?.payments_applied ?? 0) +
                Number(detail.summary?.historical_installments_paid ?? 0),
                detail.account?.currency || selected.currency
              )}</strong>
              <small>Aplicado FIFO: {money(detail.resumen?.pagos_aplicados ?? detail.summary?.payments_applied ?? 0, detail.account?.currency || selected.currency)}</small>
              <small>Según PDF (no conciliado): {money(detail.summary?.historical_installments_paid ?? 0, detail.account?.currency || selected.currency)}</small>
            </article>
          ) : (
            <article className="collection-card">
              <small>Pagos aplicados</small>
              <strong>{money(detail.resumen?.pagos_aplicados ?? detail.summary?.payments_applied, detail.account?.currency || selected.currency)}</strong>
              <small>Aplicación automática FIFO</small>
            </article>
          )}
          <article className="collection-card">
            <small>Saldo contable</small>
            <strong>{money(detail.resumen?.saldo_contable ?? detail.summary?.accounting_balance, detail.account?.currency || selected.currency)}</strong>
            <small>Saldo pendiente según aplicaciones FIFO</small>
          </article>
        </div>
        {selected.recordType === "historica" && detail.account?.initial_installment && <section className="collection-panel" style={{marginTop:18,background:"#fffaf0",borderColor:"#f2d29b"}}>
          <h2>Cuota inicial histórica (n.º 0)</h2>
          <p><strong>Importe:</strong> {money(detail.account.initial_installment.amount, detail.account.currency || selected.currency)}</p>
          <p><strong>Fecha:</strong> {dateText(detail.account.initial_installment.payment_date || detail.account.initial_installment.due_date)}</p>
          <p><strong>Estado indicado en el PDF:</strong> {detail.account.initial_installment.source_status || "No indicado"}</p>
          <p><strong>Nota:</strong> el estado del PDF es documental y no constituye una aplicación de pago. El saldo real se calcula con los pagos registrados y aplicados por FIFO.</p>
        </section>}
        <h2>{selected.recordType === "historica" ? "Obligaciones y aplicaciones FIFO" : "Plan de cuotas y aplicaciones FIFO"}</h2><div className="collection-table-wrap"><table className="collection-table">
          {Array.isArray(detail.obligaciones) ? <>
            <thead><tr><th>Obligación</th><th>Concepto</th><th>Vencimiento</th><th>Importe</th><th>Aplicado</th><th>Saldo</th><th>Estado</th></tr></thead>
            <tbody>
              {detail.obligaciones.map((o,n)=>{
                const historicalStatus = String(o.historical_status || o.source_status || "").trim().toLowerCase();
                const paidInPdf = ["pagado", "paid", "verified", "verificado"].includes(historicalStatus);
                const paidAccounting = Number(o.outstanding || 0) <= 0;
                const overdue = !paidInPdf && !paidAccounting && o.due_date && new Date(String(o.due_date).slice(0,10) + "T00:00:00") < new Date(new Date().setHours(0,0,0,0));
                const statusLabel = paidAccounting ? "Pagada" : paidInPdf ? "Pagada · PDF" : overdue ? "Vencida" : "Pendiente";
                const statusClass = paidAccounting || paidInPdf ? "collection-status-paid" : overdue ? "collection-status-overdue" : "collection-status-pending";
                return <tr key={o.key || n} className={overdue ? "collection-row-overdue" : ""}>
                  <td>{o.installment ?? (o.stage === "initial" ? "Inicial" : n+1)}</td>
                  <td>{o.concept || o.stage || "Obligación"}</td>
                  <td>{dateText(o.due_date)}</td>
                  <td>{money(o.amount, "USD")}</td>
                  <td>{money(o.paid, "USD")}</td>
                  <td>{money(o.outstanding, "USD")}</td>
                  <td><span className={`collection-status-pill ${statusClass}`} title={paidInPdf && !paidAccounting ? "Estado indicado en el PDF; no constituye una aplicación contable" : ""}>{statusLabel}</span></td>
                </tr>;
              })}
              {!detail.obligaciones.length && <tr><td colSpan="7">No hay obligaciones disponibles.</td></tr>}
            </tbody>
          </> : <>
            <thead><tr><th>N.º</th><th>Concepto</th><th>Vencimiento</th><th>Importe</th><th>Estado de origen</th></tr></thead>
            <tbody>
              {(detail.contract_plan?.plan_pagos || detail.account?.installment_plan || []).map((i,n)=>{
                const status = String(i.historical_status || i.source_status || i.status || i.etapa || "pendiente").trim().toLowerCase();
                const paid = ["pagado","paid","verified","verificado"].includes(status);
                const rawDate = i.due_date || i.fecha;
                const due = rawDate ? new Date(String(rawDate).slice(0,10) + "T00:00:00") : null;
                const today = new Date();
                today.setHours(0,0,0,0);
                const overdue = !paid && due && !Number.isNaN(due.getTime()) && due < today;
                const label = paid ? "Pagada · PDF" : overdue ? "Vencida / mora" : "Pendiente";
                const cls = paid ? "collection-status-paid" : overdue ? "collection-status-overdue" : "collection-status-pending";
                return <tr key={i.id || i.number || i.cuota || n} className={overdue ? "collection-row-overdue" : ""}>
                  <td>{i.number ?? i.cuota ?? n+1}</td>
                  <td>{i.concept || i.concepto || "Cuota"}</td>
                  <td>{dateText(rawDate)}</td>
                  <td>{money(i.amount ?? i.monto, detail.account?.currency || selected.currency)}</td>
                  <td><span className={`collection-status-pill ${cls}`} title={paid ? "Estado importado del PDF; no acredita conciliación bancaria" : ""}>{label}</span></td>
                </tr>;
              })}
              {!(detail.contract_plan?.plan_pagos || detail.account?.installment_plan || []).length && <tr><td colSpan="5">No hay plan de cuotas disponible.</td></tr>}
            </tbody>
          </>}
        </table></div>
        <h2 style={{marginTop:24}}>Pagos registrados</h2><div className="collection-table-wrap"><table className="collection-table"><thead><tr><th>Fecha</th><th>Concepto</th><th>Importe</th><th>Estado</th><th>Comprobante</th>{isAdmin && <th>Acción</th>}</tr></thead><tbody>
          {(detail.payments || []).map((p,n)=><tr key={p.id || n}><td>{dateText(p.payment_date)}</td><td>{p.concept || "Pago"}</td><td>{money(p.amount, p.currency || detail.account?.currency || selected.currency)}</td><td>{p.status === "void" ? "Anulado" : "Aplicado FIFO"}</td><td>
            {p.receipt_no || p.receipt_reference || "—"}
            {selected.recordType === "historica" && <button
              type="button"
              className="collection-link"
              style={{marginLeft:8}}
              onClick={() => printHistoricalReceipt(p)}
              title="Imprimir comprobante individual"
            ><Printer size={14} style={{verticalAlign:"middle"}}/> Imprimir</button>}
          </td>{isAdmin && <td>{p.status === "active" ? <button type="button" className="collection-link" onClick={() => voidPayment(p)}>Anular pago</button> : "—"}</td>}</tr>)}
          {!(detail.payments || []).length && <tr><td colSpan={isAdmin ? "6" : "5"}>No hay pagos registrados.</td></tr>}
        </tbody></table></div>
      </section>}

    </>}


    {paymentModal && <div role="presentation" onMouseDown={e => {
      if (e.target === e.currentTarget) setPaymentModal(false);
    }} style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(15,23,42,.62)",display:"flex",alignItems:"center",justifyContent:"center",padding:18,backdropFilter:"blur(5px)"}}>
      <section role="dialog" aria-modal="true" aria-labelledby="payment-modal-title" style={{width:"min(760px,100%)",maxHeight:"92vh",overflowY:"auto",background:"#fff",borderRadius:22,boxShadow:"0 24px 80px rgba(15,23,42,.3)",padding:26}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:16,borderBottom:"1px solid #e2e8f0",paddingBottom:18,marginBottom:20}}>
          <div>
            <div style={{fontSize:12,fontWeight:800,letterSpacing:1.3,color:"#0d72b9",textTransform:"uppercase"}}>Cobranza · Nuevo movimiento</div>
            <h2 id="payment-modal-title" style={{margin:"7px 0",fontSize:25,color:"#13243a"}}>Registrar pago</h2>
            <p style={{margin:0,color:"#64748b"}}>{detail?.account?.owner_name || detail?.sale?.client_name || selected?.displayName || "Venta seleccionada"} · Venta {detail?.account?.original_sale_number || detail?.sale?.sale_number || selected?.original_sale_number || selected?.id || "—"}</p>
            <p style={{margin:"5px 0 0",color:"#64748b",fontSize:13}}>Manzano: {detail?.account?.lot?.manzano || detail?.account?.manzano || detail?.sale?.lot?.manzano || selected?.lot?.manzano || selected?.manzano || "—"} · Lote: {detail?.account?.lot?.lote || detail?.account?.lote || detail?.sale?.lot?.lote || selected?.lot?.lote || selected?.lote || "—"}</p>
          </div>
          <button type="button" aria-label="Cerrar" onClick={()=>setPaymentModal(false)} style={{border:"1px solid #dbe3ed",background:"#fff",borderRadius:10,padding:"9px 12px",cursor:"pointer",fontSize:18}}>×</button>
        </div>
        {detailLoading ? <p>Cargando datos de la venta…</p> : !detail ? <p>No se pudo cargar el detalle. Cierra la ventana e inténtalo nuevamente.</p> :
        <form onSubmit={submitPayment}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
            <div className="collection-field"><label>Importe *</label><input required min="0.01" step="0.01" type="number" className="collection-input" value={payment.amount} onChange={e=>setPayment({...payment,amount:e.target.value})} placeholder="0,00"/></div>
            <div className="collection-field"><label>Fecha de pago *</label><input required type="date" className="collection-input" value={payment.payment_date} onChange={e=>setPayment({...payment,payment_date:e.target.value})}/></div>
            <div className="collection-field"><label>Moneda recibida</label><select className="collection-input" value={payment.currency} onChange={e=>setPayment({...payment,currency:e.target.value})}><option value="USD">USD — Dólares</option><option value="BOB">BOB — Bolivianos</option></select></div>
            {payment.currency === "BOB" && <div className="collection-field"><label>Tipo de cambio (Bs por USD) *</label><input required min="0.01" step="0.01" type="number" className="collection-input" value={payment.exchange_rate} onChange={e=>setPayment({...payment,exchange_rate:e.target.value})}/></div>}
            <div className="collection-field"><label>Método de pago *</label><select required className="collection-input" value={payment.method} onChange={e=>setPayment({...payment,method:e.target.value})}><option>Transferencia</option><option>Depósito</option><option>Efectivo</option><option>Cheque</option><option>Otro</option></select></div>
            <div className="collection-field"><label>Referencia / comprobante</label><input className="collection-input" value={payment.reference} onChange={e=>setPayment({...payment,reference:e.target.value})} placeholder="Número de operación"/></div>
            <div className="collection-field"><label>Concepto</label><input required className="collection-input" value={payment.concept} onChange={e=>setPayment({...payment,concept:e.target.value})}/></div>
            <div className="collection-field" style={{gridColumn:"1 / -1"}}><label>Notas</label><textarea className="collection-input" rows="3" value={payment.notes} onChange={e=>setPayment({...payment,notes:e.target.value})} placeholder="Observaciones adicionales"/></div>
          </div>
          {payment.currency === "BOB" && <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:12,padding:13,marginTop:18,color:"#1e40af",fontSize:13}}>
            <strong>Aplicación al contrato en USD</strong>
            <div style={{marginTop:6}}>Importe recibido: Bs {Number(payment.amount || 0).toLocaleString("es-BO",{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
            <div>Tipo de cambio: {Number(payment.exchange_rate || 0).toLocaleString("es-BO",{minimumFractionDigits:2,maximumFractionDigits:4})}</div>
            <div style={{fontSize:18,fontWeight:800,marginTop:6}}>Se descontará: {money((Number(payment.amount || 0) / Number(payment.exchange_rate || 1)), "USD")}</div>
          </div>}
          <div style={{background:"#fff8e6",border:"1px solid #f4d58d",borderRadius:12,padding:13,marginTop:18,color:"#854d0e",fontSize:13}}>
            Al confirmar, el pago se aplicará automáticamente a las obligaciones pendientes más antiguas. No se permite registrar importes superiores al saldo pendiente.
          </div>
          {error && <p role="alert" style={{background:"#fff1f2",color:"#be123c",padding:12,borderRadius:10,marginTop:14}}>{error}</p>}
          <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:22,flexWrap:"wrap"}}>
            <button type="button" className="collection-btn" onClick={()=>setPaymentModal(false)}>Cancelar</button>
            <button type="submit" className="collection-btn primary" disabled={loading || detailLoading}>{loading ? "Guardando…" : "Confirmar registro de pago"}</button>
          </div>
        </form>}
      </section>
    </div>}

    {tab === "reportes" && <>
      <section className="collection-panel">
        <h2 style={{marginTop:0}}>Reporte de pagos y cobranzas</h2>
        <p>Filtra los movimientos por fecha efectiva de pago, cliente, venta, lote y origen de cartera.</p>
        <div className="collection-grid" style={{gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",marginTop:18}}>
          <div className="collection-field"><label>Fecha desde</label><input type="date" className="collection-input" value={reportFrom} onChange={e=>setReportFrom(e.target.value)}/></div>
          <div className="collection-field"><label>Fecha hasta</label><input type="date" className="collection-input" min={reportFrom || undefined} value={reportTo} onChange={e=>setReportTo(e.target.value)}/></div>
          <div className="collection-field"><label>Cliente / venta / lote</label><input className="collection-input" value={reportClient} onChange={e=>setReportClient(e.target.value)} placeholder="Nombre, venta, manzano o lote"/></div>
          <div className="collection-field"><label>Origen</label><select className="collection-input" value={reportOrigin} onChange={e=>setReportOrigin(e.target.value)}><option value="todos">Todos</option><option value="actual">Ventas actuales</option><option value="historica">Ventas históricas</option></select></div>
        </div>
        <div className="collection-tools" style={{marginTop:18}}>
          <button className="collection-btn primary" onClick={generatePaymentReport} disabled={reportLoading}>{reportLoading ? "Generando…" : <><Search size={16}/> Generar reporte</>}</button>
          <button className="collection-btn" onClick={()=>{setReportFrom("");setReportTo("");setReportClient("");setReportOrigin("todos");setReportRows([]);setReportGenerated(false);setNotice("");}}>Limpiar filtros</button>
        </div>
      </section>
      {reportGenerated && <section className="collection-panel">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:16}}>
          <div><h2 style={{margin:0}}>Resultados · {reportRows.length} movimiento(s)</h2><p style={{margin:"6px 0 0"}}>Los pagos anulados se identifican y no se consideran cobranzas activas.</p></div>
          <div className="collection-tools"><button className="collection-btn" onClick={exportPaymentReport} disabled={!reportRows.length}><FileText size={15}/> Exportar CSV</button><button className="collection-btn" onClick={printPaymentReport} disabled={!reportRows.length}><Printer size={15}/> Imprimir</button></div>
        </div>
        <div className="collection-table-wrap"><table className="collection-table"><thead><tr><th>Fecha</th><th>Cliente</th><th>Venta</th><th>Manzano / lote</th><th>Recibido</th><th>Cambio</th><th>Equivalente USD</th><th>Método</th><th>Estado</th><th>Origen</th></tr></thead><tbody>
          {reportRows.map(r=><tr key={r.id}><td>{dateText(r.payment_date)}</td><td><strong>{r.client}</strong></td><td>{r.sale}</td><td>{r.manzano} / {r.lote}</td><td>{money(r.amount,r.currency)}</td><td>{r.currency==="BOB" ? Number(r.exchange_rate || 0).toLocaleString("es-BO",{minimumFractionDigits:2,maximumFractionDigits:4}) : "—"}</td><td>{money(r.amount_usd,"USD")}</td><td>{r.method}</td><td><span className={`collection-status-pill ${r.status==="Activo"?"collection-status-paid":"collection-status-overdue"}`}>{r.status}</span></td><td><span className="collection-badge">{r.origin}</span></td></tr>)}
          {!reportRows.length && <tr><td colSpan="10" className="collection-empty">No hay pagos para los filtros seleccionados.</td></tr>}
        </tbody></table></div>
      </section>}
    </>}
    {tab === "historico" && <HistoricalSalesPage/>}
  </div>;
}
